/** The replace flow: configure → preview a sample → dry run → apply.
 *
 * The gating is deliberate and enforced by the backend as well as here: applying
 * requires a completed dry run of the IDENTICAL operation (same result set, same
 * find, same replacement, same selection). Changing any field invalidates the
 * dry run and the Apply button goes away again — reviewing one operation must
 * never authorise a different one.
 *
 * "Preview" and "dry run" are different things and are labelled as such: the
 * preview is a 25-row SAMPLE for iterating on a pattern; the dry run walks the
 * whole selection and is what you approve.
 */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import * as api from '@/api/contentSearch';
import { isSearchExpired } from '@/api/contentSearch';
import type {
  FindSpec,
  PreviewResults,
  ReplaceSelection,
  ReplaceOutcomeCode,
} from '@/api/contentSearch';
import { ConsoleApiError } from '@/api/client';
import { useSnackbar } from '@/components/Snackbar';

import { OutcomeSummary, PreviewRows } from './ReplacePreviewPanel';

const MIN_REASON = 10;

export interface ReplaceDialogProps {
  open: boolean;
  onClose: () => void;
  searchKey: string;
  selection: ReplaceSelection;
  selectedCount: number;
  /** Rows in the selection whose field cannot be written (identity fields). */
  readOnlyCount: number;
  onDispatched: (taskId: string, dryRun: boolean) => void;
  /** Re-run the search (with `refresh=1`). Offered inline when the pinned set
   *  has expired, so recovery is one click rather than "close the dialog, work
   *  out what happened, search again, reselect".
   *
   *  Note the dialog does NOT wait for the re-run to land: the batch menu
   *  snapshots this dialog as an element at click time, so it cannot receive a
   *  live "the pin is fresh again" prop. Clearing optimistically is
   *  self-correcting — the next action either succeeds against the new pin, or
   *  fails with `search_expired` again and restores the banner. `search_key` is
   *  derived from the query, so it stays valid across the re-run. */
  onRerunSearch: () => void;
}

type Mode = 'matched' | 'other';

export function ReplaceDialog({
  open,
  onClose,
  searchKey,
  selection,
  selectedCount,
  readOnlyCount,
  onDispatched,
  onRerunSearch,
}: ReplaceDialogProps) {
  const snackbar = useSnackbar();
  const [mode, setMode] = useState<Mode>('matched');
  const [findKind, setFindKind] = useState<'literal' | 'regex'>('literal');
  const [findValue, setFindValue] = useState('');
  const [ignoreCase, setIgnoreCase] = useState(true);
  const [replacement, setReplacement] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<PreviewResults | null>(null);
  const [dryRunTaskId, setDryRunTaskId] = useState<string | null>(null);
  /** Set when the backend reports `search_expired`. Held in state rather than
   *  flashed as a snackbar: the recovery is an action, and a toast that scrolls
   *  away leaves the operator staring at a dialog whose buttons all fail. */
  const [expired, setExpired] = useState(false);

  // Memoised, not derived inline: this object is a dependency of `opIdentity`,
  // and a fresh reference on every render would re-fire the invalidation effect
  // below continuously — clearing the dry-run receipt as fast as it arrives, so
  // Apply could never become enabled.
  const find: FindSpec | undefined = useMemo(
    () =>
      mode === 'other' && findValue
        ? { kind: findKind, value: findValue, ignore_case: ignoreCase }
        : undefined,
    [mode, findKind, findValue, ignoreCase],
  );

  /** Identity of the operation. Any change invalidates a completed dry run —
   *  the backend enforces the same rule (it re-derives this signature server
   *  side); this just stops the UI from offering an Apply the backend would
   *  reject with 409. */
  const opIdentity = useMemo(
    () => JSON.stringify({ searchKey, selection, find, replacement }),
    [searchKey, selection, find, replacement],
  );
  useEffect(() => {
    setDryRunTaskId(null);
    setPreview(null);
  }, [opIdentity]);

  useEffect(() => {
    setExpired(false);
  }, [searchKey]);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setDryRunTaskId(null);
      setReason('');
      setExpired(false);
    }
  }, [open]);

  const body = useMemo(
    () => ({
      search_key: searchKey,
      selection,
      ...(find ? { find } : {}),
      replacement,
    }),
    [searchKey, selection, find, replacement],
  );

  const errorText = (e: unknown) =>
    e instanceof ConsoleApiError
      ? ((e.body as { detail?: string } | undefined)?.detail ?? e.message)
      : (e as Error).message;

  /** An expired pin is not an error the operator can act on by reading it — it
   *  becomes an inline banner with a button. Everything else is a snackbar. */
  const handleError = (e: unknown) => {
    if (isSearchExpired(e)) {
      setExpired(true);
      setPreview(null);
      setDryRunTaskId(null);
      return;
    }
    snackbar.show(errorText(e), 'error');
  };

  const previewMut = useMutation({
    mutationFn: () => api.preview(body),
    onSuccess: setPreview,
    onError: handleError,
  });

  const dryRunMut = useMutation({
    mutationFn: () => api.replace({ ...body, dry_run: true }),
    onSuccess: (r) => {
      setDryRunTaskId(r.task_id);
      onDispatched(r.task_id, true);
      snackbar.show(
        `Dry run started over ${r.processed.toLocaleString()} field(s). Watch it in Background Tasks.`,
        'info',
      );
    },
    onError: handleError,
  });

  const applyMut = useMutation({
    mutationFn: () => api.replace({ ...body, dry_run: false, reason }),
    onSuccess: (r) => {
      onDispatched(r.task_id, false);
      snackbar.show(`Replacing ${r.processed.toLocaleString()} field(s) in the background.`, 'success');
      onClose();
    },
    onError: handleError,
  });

  const busy = previewMut.isPending || dryRunMut.isPending || applyMut.isPending;
  // An empty replacement is legitimate (it deletes the match), so the only
  // requirement is that we know WHAT to look for.
  const canPreview = mode === 'matched' || findValue !== '';
  const reasonOk = reason.trim().length >= MIN_REASON;
  const writable = selectedCount - readOnlyCount;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Replace in {selectedCount.toLocaleString()} field(s)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {expired && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    onRerunSearch();
                    setExpired(false);
                  }}
                >
                  Re-run search
                </Button>
              }
            >
              <AlertTitle>That search has expired</AlertTitle>
              Results are held for a few minutes so they cannot change while you review a
              replacement. Nothing is wrong with your query — re-run it, then select the rows
              again. Your pattern and replacement below are kept.
            </Alert>
          )}

          {readOnlyCount > 0 && (
            <Alert severity="info">
              {readOnlyCount.toLocaleString()} of the selected field(s) are identity fields
              (name / code name / alias) and will be skipped — they are searchable but not
              replaceable, because renaming them can break references and unique constraints.
              {writable > 0
                ? ` ${writable.toLocaleString()} field(s) will be considered.`
                : ' Nothing in this selection can be changed.'}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              What to replace
            </Typography>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
              <FormControlLabel
                value="matched"
                control={<Radio size="small" />}
                label="What I searched for (every matched fragment)"
              />
              <FormControlLabel
                value="other"
                control={<Radio size="small" />}
                label="Something else"
              />
            </RadioGroup>
          </Box>

          {mode === 'other' && (
            <Stack spacing={1.5}>
              <RadioGroup
                row
                value={findKind}
                onChange={(e) => setFindKind(e.target.value as 'literal' | 'regex')}
              >
                <FormControlLabel value="literal" control={<Radio size="small" />} label="Text" />
                <FormControlLabel
                  value="regex"
                  control={<Radio size="small" />}
                  label="Regular expression"
                />
              </RadioGroup>
              <TextField
                label={findKind === 'regex' ? 'Pattern to find' : 'Text to find'}
                value={findValue}
                onChange={(e) => setFindValue(e.target.value)}
                fullWidth
                size="small"
                InputProps={{ sx: { fontFamily: 'ui-monospace, monospace' } }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={ignoreCase}
                    onChange={(e) => setIgnoreCase(e.target.checked)}
                  />
                }
                label="Ignore case"
              />
              <Alert severity="warning" variant="outlined">
                This searches for something other than what the grid matched, so some selected
                fields may contain it more times than you expect — or not at all. The dry run
                reports both.
              </Alert>
            </Stack>
          )}

          <TextField
            label="Replace with"
            value={replacement}
            onChange={(e) => setReplacement(e.target.value)}
            fullWidth
            size="small"
            multiline
            maxRows={6}
            InputProps={{ sx: { fontFamily: 'ui-monospace, monospace' } }}
            helperText={
              mode === 'other' && findKind === 'regex'
                ? 'Backreferences allowed: \\1, \\2, \\g<name>. Leave empty to delete the match.'
                : 'Leave empty to delete the matched text.'
            }
          />

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => previewMut.mutate()}
              disabled={busy || !canPreview || expired}
            >
              Preview first 25
            </Button>
            <Button
              variant="outlined"
              onClick={() => dryRunMut.mutate()}
              disabled={busy || !canPreview || expired}
            >
              Run full dry run
            </Button>
            {dryRunTaskId && <Chip size="small" color="success" label="Dry run started" />}
          </Stack>
          {busy && <LinearProgress />}

          {preview && (
            <Box>
              <OutcomeSummary summary={preview.summary} />
              {preview.sampled && (
                <Typography variant="caption" color="text.secondary">
                  Showing the first {preview.previewed} of {preview.total_selected.toLocaleString()}{' '}
                  selected field(s). This is a sample — run the full dry run before applying.
                </Typography>
              )}
              <PreviewRows rows={preview.results} />
            </Box>
          )}

          <Divider />

          <Alert severity={dryRunTaskId ? 'success' : 'info'}>
            <AlertTitle>
              {dryRunTaskId ? 'Ready to apply' : 'A full dry run is required before applying'}
            </AlertTitle>
            {dryRunTaskId
              ? 'Check the dry-run report in Background Tasks, then apply. Changing anything above starts over.'
              : 'The dry run walks every selected field and reports exactly what would change. Applying without one is refused.'}
          </Alert>

          <TextField
            label="Why are you making this change?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            required
            error={reason.length > 0 && !reasonOk}
            helperText={
              reason.length > 0 && !reasonOk
                ? `At least ${MIN_REASON} characters.`
                : 'Recorded in the audit log and in each record’s change history.'
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => applyMut.mutate()}
          disabled={busy || !dryRunTaskId || !reasonOk || writable === 0 || expired}
        >
          Apply to {writable.toLocaleString()} field(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export type { ReplaceOutcomeCode };
